/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FaBars } from "react-icons/fa6";
import "../../static/css/common/header.css";
import { Helmet } from "react-helmet-async";
import { api } from "@/api";
import { useTranslation } from "react-i18next";
import { useProductCatalogue } from "@/lib/productCatalogue";
import { AddLangToPath } from "@/helper/AddLanguagePath";
import { getHreflangAlternates } from "@/utils/canonicalUrl";
import { URL_LANGUAGES } from "@/lib/constants";
import { MenuBackIcon } from "@/components/common/icons";

import CurrencyConverter from "@/helper/CurrencyConverter";
import { safeSetLang } from "@/helper/safeLocalStorage";
import { rememberLanguageForForum } from "@/helper/languageCookie";
import {
  getCartUrl,
  getAccountUrl,
  getSignInEntryUrl,
  FORUM_PATH,
} from "@/utils/siteConfig";
import { useForumLinkHref } from "@/lib/forum-access";
import {
  LanguageSwitcher,
  useLanguageInit,
  ProductDropdown,
  NavDropdown,
  MobileNavbar,
  CartIcon,
  useLogOut,
  BREAKPOINT,
  LANGUAGES,
} from "./HeaderComponents";
import { isShownInHeaderMenu } from "@/lib/productCatalogue/productCatalogue";
import type { NavItem, NavDropdownSection } from "./HeaderComponents";
import { useWindowWidth } from "@/hooks/useWindowWidth";
import { completeLoginRedirect } from "@/utils/postLoginRedirect";
import { useForumSessionSync } from "@/hooks/useForumSessionSync";
import { STATIC_ASSETS } from "@/constants/staticAssets";

const Header: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useLanguageInit();
  const windowWidth = useWindowWidth(BREAKPOINT);
  const [openSupport, setOpenSupport] = useState(false);
  const [openResources, setOpenResources] = useState(false);
  const [openCompany, setOpenCompany] = useState(false);

  const { isuser_loggedin } = api.auth.getShoppingCartLoginStatus();

  // T-1: the header is where sign-in state is shown, so it is where the forum
  // session is checked. The hook does nothing at all unless a forum cookie was
  // minted in this browser, so it costs a signed-out visitor - and a customer
  // who has never opened the forum - no request whatsoever.
  useForumSessionSync();

  // T-1: resolves to the display-name collection screen instead of the
  // forum itself when the customer is signed in but hasn't chosen a public
  // forum name yet (decision D-6). Unchanged for signed-out visitors.
  const forumLinkHref = useForumLinkHref(FORUM_PATH, AddLangToPath);

  const navItems: NavItem[] = useMemo(
    () => [
      {
        title: t("header.support.title"),
        items: [
          {
            title: t("header.support.forum.title"),
            link: forumLinkHref,
            forceHardNav: true, // Traefik routes /forums to NodeBB; React Router bypasses Traefik
          },
          {
            title: t("header.support.technical_support.title"),
            link: AddLangToPath("/product-support/"),
          },
          {
            title: t("header.support.lost_license.title"),
            link: AddLangToPath("/lost-license-key/"),
          },
        ],
      },
      {
        title: t("header.resources.title"),
        items: [
          {
            title: t("header.resources.articles.title"),
            link: AddLangToPath("/articles"),
          },
          {
            title: t("header.resources.video_tutorials.title"),
            link: AddLangToPath("/tutorials-manuals"),
          },
        ],
      },
      {
        title: t("header.company.title"),
        items: [
          {
            title: t("header.company.contact.title"),
            link: AddLangToPath("/contact-us/"),
          },
          {
            title: t("header.company.about.title"),
            link: AddLangToPath("/about-deskshare/"),
          },
        ],
      },
    ],
    [t, forumLinkHref],
  );

  const [mobileNavbarItems, setMobileNavbarItems] =
    useState<NavItem[]>(navItems);

  // Runs once per visit. Calling this straight from the render body ran it
  // again on every state change — every menu toggle, dropdown, language
  // change and window resize — each time re-reading stored currency and,
  // before the daily cache exists, able to fire overlapping requests.
  useEffect(() => {
    void CurrencyConverter();
  }, []);

  useEffect(() => {
    if (windowWidth > BREAKPOINT) {
      setOpenMenu(false);
      document.body.style.overflow = "auto";
      return;
    }
    document.body.style.overflow = openMenu ? "hidden" : "auto";
  }, [windowWidth, openMenu]);

  // IND-30: the header no longer performs its own fetch. It reads the shared
  // catalogue module — the same one the support forms use — and keeps only its
  // own presentation filter. refresh: false keeps today's behaviour exactly; see
  // the note on the throttle in useProductCatalogue.
  const { productData } = useProductCatalogue({ refresh: false });

  useEffect(() => {
    let cancelled = false;

    const buildNavItems = (): void => {
      const data = productData;
      if (cancelled || !data) return;

      const allProducts = Object.values(data).flatMap((products) => products);
      const eligibleProducts = allProducts.filter(
        // Session 423 / F-13: the catalogue says which products the header
        // offers; this file no longer carries two product names.
        isShownInHeaderMenu,
      );

      const uniqueProducts = new Map<string, { title: string; link: string }>();
      eligibleProducts.forEach((product) => {
        if (!uniqueProducts.has(product.product_slug)) {
          uniqueProducts.set(product.product_slug, {
            title: product.product_name,
            link: AddLangToPath(`/${product.product_slug}`),
          });
        }
      });

      if (!cancelled) {
        setMobileNavbarItems([
          {
            title: t("header.product"),
            items: Array.from(uniqueProducts.values()),
          },
          ...navItems,
        ]);
      }
    };

    buildNavItems();

    return () => {
      cancelled = true;
    };
  }, [t, navItems, productData]);

  // Bug 6 fix: logo click on homepage now triggers a full page reload so all state
  // is cleared and homepage data is re-fetched. window.location.reload() replaces
  // the previous navigate(replace:true) which was a no-op (SPA route replacement).
  // This also closes HANDOFF DD-5 (homepage re-fetch awaiting product decision).
  const closeMobileMenuRef = React.useRef<(() => void) | null>(null);
  const isMobileRef = React.useRef<boolean>(false);

  const handleLogoClick = useCallback(
    (e: React.MouseEvent): void => {
      const homePath = AddLangToPath("/");
      if (location.pathname === homePath) {
        e.preventDefault();
        window.location.reload(); // full reload — clears state, re-runs data fetch
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
      if (isMobileRef.current) closeMobileMenuRef.current?.();
    },
    [location.pathname],
  );

  const handleLanguage = useCallback(
    (lang: string): void => {
      if (!lang) return;

      const langExists = LANGUAGES.some((l) => l.id === lang);
      if (langExists) {
        setSelectedLanguage(lang);
        safeSetLang(lang);
        // The visitor picked it: the support forum opens in it too.
        rememberLanguageForForum(lang);

        let mypathArr: string[] = window.location.pathname.split("/");
        if (LANGUAGES.some((l) => l.id === mypathArr[1])) {
          mypathArr = mypathArr.slice(2);
        }
        const mypath = mypathArr.filter(Boolean).join("/");

        // AF-2: window.location.replace() prevents the current URL being added to
        // browser history (no spurious back-button entry for the pre-language-switch URL).
        // A hard reload is architecturally required here: this codebase uses URL-based
        // language routing and i18next-http-backend (loads locale JSON on demand).
        window.location.replace(
          lang === "en" ? `/${mypath}` : `/${lang}/${mypath}`,
        );
      } else {
        setSelectedLanguage("en");
        safeSetLang("en");
      }
    },
    [setSelectedLanguage],
  );

  // The effect above owns the page scroll lock. Setting it here as well meant
  // two places decided it, and it was being set from inside a state updater —
  // which React is free to run more than once.
  const openMenuBar = useCallback((): void => {
    setOpenMenu((prev) => !prev);
  }, []);

  const handleNavClick = useCallback(() => {
    if (windowWidth < BREAKPOINT) {
      openMenuBar();
    }
  }, [windowWidth, openMenuBar]);

  const closeMobileMenu = useCallback(() => {
    setOpenMenu(false);
  }, []);
  // DD-3: keep ref in sync so handleLogoClick (declared before this) can call it
  closeMobileMenuRef.current = closeMobileMenu;

  const navDropdownSections: NavDropdownSection[] = useMemo(
    () => [
      {
        title: "support",
        openState: openSupport,
        setOpenState: setOpenSupport,
        links: [
          {
            to: forumLinkHref,
            titleKey: "header.support.forum.title",
            descKey: "header.support.forum.description",
            isDjangoPage: true, // Traefik routes /forums → NodeBB; must be <a href>
          },
          {
            to: AddLangToPath("/product-support/"),
            titleKey: "header.support.technical_support.title",
            descKey: "header.support.technical_support.description",
          },
          {
            to: AddLangToPath("/lost-license-key/"),
            titleKey: "header.support.lost_license.title",
            descKey: "header.support.lost_license.description",
          },
        ],
      },
      {
        title: "resources",
        openState: openResources,
        setOpenState: setOpenResources,
        links: [
          {
            to: AddLangToPath("/articles"),
            titleKey: "header.resources.articles.title",
            descKey: "header.resources.articles.description",
          },
          {
            to: AddLangToPath("/tutorials-manuals"),
            titleKey: "header.resources.video_tutorials.title",
            descKey: "header.resources.video_tutorials.description",
          },
        ],
      },
      {
        title: "company",
        openState: openCompany,
        setOpenState: setOpenCompany,
        links: [
          {
            to: AddLangToPath("/contact-us/"),
            titleKey: "header.company.contact.title",
            descKey: "header.company.contact.description",
          },
          {
            to: AddLangToPath("/about-deskshare/"),
            titleKey: "header.company.about.title",
            descKey: "header.company.about.description",
          },
        ],
      },
    ],
    [openSupport, openResources, openCompany, forumLinkHref],
  );

  const isMobile = windowWidth < BREAKPOINT;
  // DD-3: keep ref in sync so handleLogoClick can read current value
  isMobileRef.current = isMobile;

  // DD-15: Both desktop and mobile carts now use getCartUrl() — consistent with mobile
  // and with the rest of the codebase. basket.aspx was a legacy hardcoded URL.
  const cartHref = getCartUrl(selectedLanguage);

  // T-2: the host branch is gone. getAccountUrl and getDashboardUrl returned
  // the same string on every host after T-2, so the conditional chose between
  // two identical values - which reads as meaningful to the next developer and
  // invites one arm being edited on the assumption the other is different.
  const authHref = isuser_loggedin
    ? getAccountUrl(selectedLanguage)
    : getSignInEntryUrl(selectedLanguage);

  // T-2 / VM-3 (Session 484): the shop is a token consumer. A plain link
  // navigates cross-origin without minting a token, so the customer arrives
  // with whatever session the shop already holds - none, or another
  // customer's after an account switch. My Account and both cart icons
  // therefore hand the website sign-in to the shop the same way.
  //
  // A Ctrl/Shift/Cmd/Alt or middle click is left to the browser: it opens a
  // new tab (or window, or download) with the plain address, as a link should.
  // The handler used to cancel every click, so Ctrl-click navigated the
  // current tab instead of opening a new one.
  //
  // What must still not be handed off: a signed-out visitor's click (a normal
  // navigation), and any modified or non-primary click.
  const handOffToShop = useCallback(
    async (event: React.MouseEvent<HTMLAnchorElement>, target: string) => {
      if (!isuser_loggedin) return;
      if (
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      event.preventDefault();

      // completeLoginRedirect never throws - it reports failure by returning
      // ok: false, and on that branch it has deliberately not navigated either.
      // preventDefault has already cancelled the browser's own navigation, so
      // without this fallback the link does nothing at all, on every click,
      // with the only record in Sentry. Falling back to a plain navigation
      // lands the customer on the shop signed out, which is worse than the
      // hand-off and far better than a dead link.
      const result = await completeLoginRedirect(target);
      if (!result.ok) {
        window.location.href = target;
      }
    },
    [isuser_loggedin],
  );

  const handleAuthClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) =>
      void handOffToShop(event, authHref),
    [handOffToShop, authHref],
  );

  const handleCartClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) =>
      void handOffToShop(event, cartHref),
    [handOffToShop, cartHref],
  );

  const { handleLogOut, isLoggingOut } = useLogOut();

  return (
    <header>
      <Helmet>
        <html lang={selectedLanguage}></html>
        <meta name="description" content={t("meta.site_description")} />
        <meta property="og:title" content="Deskshare" />
        <meta property="og:site_name" content="Deskshare" />
        <meta property="og:description" content={t("meta.site_description")} />
        <meta
          property="og:image"
          content={`${window.location.origin}${STATIC_ASSETS.logoPng}`}
        />
        <meta property="og:image:width" content="512" />
        <meta property="og:image:height" content="512" />
        {/* IND-21: one owner per tag.
            The header now writes only what is the same on every page — the
            site-level social tags and the language alternates below. The
            address tag, and the per-page social tags that go with it, belong to
            PageMeta, which is the component that knows what page it is on.

            Both used to write the address tag. The tag library keeps only one,
            and which one wins depends on which mounts first. That was harmless
            while the two values matched, and stops being harmless the moment a
            page needs a different one — which is exactly what the review pages
            need, since a review page and its product page currently each
            declare themselves the original. */}
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Deskshare" />
        <meta name="twitter:description" content={t("meta.site_description")} />
        <meta
          name="twitter:image"
          /* Item 12: this asked for /images/deskshare_logo512.png, a file that
             is not under that folder in this repository — it is at the root —
             so on the live site the address redirected to the old site's front
             page and this entry had no sharing image at all. It now points at
             the same logo the other two social tags use. */
          content={`${window.location.origin}${STATIC_ASSETS.logoPng}`}
        />
        {/* hreflang alternate links — one per locale including x-default and en.
            Tells Google each URL is a language variant, not duplicate content.
            getHreflangAlternates() strips any existing lang prefix from the
            current pathname before rebuilding each locale URL — so visiting
            /de/screen-recorder produces /fr/screen-recorder for French, never
            /fr/de/screen-recorder. */}
        {getHreflangAlternates(
          window.location.pathname,
          window.location.origin,
          URL_LANGUAGES,
        ).map(({ hrefLang, href }) => (
          <link
            key={hrefLang}
            rel="alternate"
            hrefLang={hrefLang}
            href={href}
          />
        ))}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Deskshare",
            url: "https://www.deskshare.com",
            logo: `https://www.deskshare.com${STATIC_ASSETS.logoPng}`,
            sameAs: [
              "https://www.facebook.com/people/Deskshare/100064110930535/",
              "https://www.youtube.com/@DeskShareInc",
              "https://www.linkedin.com/company/deskshare",
            ],
          })}
        </script>
      </Helmet>
      <nav
        className="navigation"
        aria-label={t("accessibility.main_navigation")}
        style={
          isMobile && openMenu
            ? { position: "fixed", width: "100%", zIndex: "100" }
            : { position: "relative", width: "100%" }
        }
      >
        <div className="navbar">
          {isMobile && (
            <button
              type="button"
              className="menu-bar"
              // A button is activated by Enter and Space natively, so a
              // keydown handler here fired the toggle twice and the menu never
              // opened for keyboard users. One handler also means the page
              // scroll lock is applied on every activation, not just some.
              onClick={openMenuBar}
              aria-label={t("accessibility.menu")}
              aria-expanded={openMenu}
            >
              {!openMenu ? (
                <FaBars className="svg-inline--fa fa-bars menu-button" />
              ) : (
                <MenuBackIcon className="menu-button" />
              )}
            </button>
          )}
          <div className="content-left">
            <Link
              to={AddLangToPath("/")}
              className="deskshare-logo"
              onClick={handleLogoClick}
            >
              {/* Session 519 review, VM-2: the header logo is above the fold
                on every page, so it is requested at once rather than after
                script decides it is visible. width and height let the browser
                reserve its box before the file arrives; .logo sets the shown
                width and height: auto keeps the 231:73 shape. */}
              <img
                className="logo"
                alt={t("accessibility.deskshare_logo")}
                src={STATIC_ASSETS.logoSvg}
                width={231}
                height={73}
                loading="eager"
                fetchPriority="high"
              />
            </Link>
          </div>

          <div className={!openMenu || !isMobile ? "content" : "hidden"}>
            <div className="content-center">
              <ProductDropdown
                productData={productData}
                onNavClick={handleNavClick}
              />
              <Link
                to={AddLangToPath("/download")}
                className="items"
                id="download"
              >
                <span>{t("header.download")}</span>
              </Link>
              <NavDropdown
                sections={navDropdownSections}
                selectedLanguage={selectedLanguage}
                onNavClick={handleNavClick}
              />
            </div>
            <div className="auth">
              <CartIcon href={cartHref} onClick={handleCartClick} />
              <span className="auth-separator" aria-hidden="true" />
              <a className="signin" href={authHref} onClick={handleAuthClick}>
                {isuser_loggedin ? t("header.my_account") : t("header.sign_in")}
              </a>
              {isuser_loggedin && (
                <>
                  <span
                    className="auth-separator auth-logout"
                    aria-hidden="true"
                  />
                  <button
                    type="button"
                    className="signin auth-logout"
                    onClick={handleLogOut}
                    disabled={isLoggingOut}
                  >
                    {t("header.log_out")}
                  </button>
                </>
              )}
            </div>
          </div>

          {isMobile && (
            <CartIcon
              href={cartHref}
              className="cart-btn mobile-cart-btn"
              onClick={handleCartClick}
            />
          )}
          <LanguageSwitcher
            selectedLanguage={selectedLanguage}
            onLanguageChange={handleLanguage}
          />
        </div>

        <button
          type="button"
          className={`overlay-button ${!openMenu || !isMobile ? "" : "blur-background"}`}
          onClick={() => setOpenMenu(false)}
          aria-label={
            openMenu
              ? t("accessibility.close_menu")
              : t("accessibility.open_menu")
          }
          tabIndex={openMenu && isMobile ? 0 : -1}
        ></button>

        {isMobile && openMenu && (
          <MobileNavbar
            productData={productData}
            navItems={navItems}
            mobileNavbarItems={mobileNavbarItems}
            isLoggedIn={isuser_loggedin}
            onClose={closeMobileMenu}
          />
        )}
      </nav>
    </header>
  );
};

export default Header;
